import { Dispatch, SetStateAction, useEffect, useMemo } from 'react';
import { useRouter } from '@/hooks';
import { useGetAlarmLevelSummaryQuery } from '@/__generated__/graphql';
import { ALARM_LEVEL_COLORS } from '@/theme/colors';
import { TemporalAnalysisGraphs } from './TemporalAnalysisGraphs/TemporalAnalysisGraphs';
import { Box } from '@mui/material';
import { PageLoader } from '@/components/shared/PageLoader';

type Props = {
  setSelectedDeployments: Dispatch<SetStateAction<string[]>>;
  selectedDeployments: string[];
};

export const ComponentHealthGraph = ({ setSelectedDeployments, selectedDeployments }: Props) => {
  const router = useRouter();
  const { platformId } = router.query;

  const { loading: loadingAlarmLevelSummary, data: alarmLevelSummaryData } =
    useGetAlarmLevelSummaryQuery({
      variables: {
        input: {
          platformId: platformId ? platformId.toString() : '',
        },
      },
      skip: !platformId || platformId === '',
    });

  const alarmLevelSummary = useMemo(() => {
    const alarmLevelSummarys = alarmLevelSummaryData?.getAlarmLevelSummary;
    if (!alarmLevelSummarys) return {};

    const transformedData = alarmLevelSummarys.flatMap((alarmLevel) => {
      if (!alarmLevel || !alarmLevel.total) return [];
      if (
        selectedDeployments.length > 0 &&
        !selectedDeployments.includes(alarmLevel.structureName || '')
      )
        return [];

      // only return alarm levels that have type defined (alarm level 1 2 3 etc)
      const alarmLevelValues = alarmLevel.total.filter((level) => level.type !== null);

      if (alarmLevelValues.length === 0) return [];

      // format the alarm levels to work with our graph component by making the count value against the alarm level type
      const alarmLevels = alarmLevelValues.reduce(
        (accumulator: { [key: string]: number }, { type, count }) => {
          if (type) accumulator[type] = count;
          return accumulator;
        },
        {}
      );

      return {
        name: alarmLevel.structureName || '',
        ...alarmLevels,
      };
    });

    // alarm levels are defined statically in the theme colors file
    return {
      values: transformedData,
      colors: ALARM_LEVEL_COLORS,
      title: 'Line alarm level breakdown',
    };
  }, [alarmLevelSummaryData?.getAlarmLevelSummary, selectedDeployments]);

  useEffect(() => {
    if (alarmLevelSummary.values) {
      const newSelectedDeployments = alarmLevelSummary.values.map((data) => data.name);

      const arraysAreEqual =
        newSelectedDeployments.length === selectedDeployments.length &&
        newSelectedDeployments.every((value, index) => value === selectedDeployments[index]);

      if (!arraysAreEqual) {
        setSelectedDeployments(newSelectedDeployments);
      }
    }
  }, [alarmLevelSummary.values, selectedDeployments, setSelectedDeployments]);

  return (
    <>
      {loadingAlarmLevelSummary ? (
        <Box sx={{ position: 'relative', height: '100%' }}>
          <PageLoader />
        </Box>
      ) : (
        <>
          {alarmLevelSummary && alarmLevelSummary.values && (
            <TemporalAnalysisGraphs
              title={alarmLevelSummary.title + ' - Overall platform'}
              data={alarmLevelSummary.values}
              legendData={alarmLevelSummary.colors}
              showUnit={false}
            />
          )}
        </>
      )}
    </>
  );
};
